import { Capacitor, registerPlugin } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import {
  deleteAccountData,
  exchangeAppleIdentity,
  fetchAccountEntitlement,
  revokeAccountSession,
  type AccountSession,
} from './accountApi';
import { configureAccountDataSession } from '../api/client';

export const AUTH_STORAGE_KEY = 'daykeeper.v1.auth-session';

export type GuestSession = { provider: 'guest' };
export type AppleSession = {
  provider: 'apple';
  userIdentifier: string;
  email?: string;
  accountSession?: AccountSession;
};
export type AuthSession = GuestSession | AppleSession;

type AppleSessionResult = { session: AppleSession | null; identityToken?: string };
type ServerSessionResult = { session: AccountSession | null };

interface AppleSignInPlugin {
  signIn(): Promise<AppleSessionResult>;
  restoreSession(): Promise<AppleSessionResult>;
  clearSession(): Promise<void>;
  setServerSession(session: AccountSession): Promise<void>;
  getServerSession(): Promise<ServerSessionResult>;
  clearServerSession(): Promise<void>;
}

interface AuthStorage {
  get(): Promise<string | null>;
  set(value: string): Promise<void>;
  remove(): Promise<void>;
}

type AuthServiceDependencies = {
  isNative: boolean;
  storage: AuthStorage;
  appleSignIn: AppleSignInPlugin;
  exchangeAppleIdentity(identityToken: string): Promise<AccountSession>;
  fetchAccountEntitlement(session: AccountSession): Promise<AccountSession>;
  revokeAccountSession(session: AccountSession): Promise<void>;
  deleteAccountData(session: AccountSession): Promise<void>;
  configureAccountDataSession(session: AccountSession | null): void;
};

const AppleSignIn = registerPlugin<AppleSignInPlugin>('AppleSignIn');

const defaultStorage: AuthStorage = {
  get: async () => Capacitor.isNativePlatform()
    ? (await Preferences.get({ key: AUTH_STORAGE_KEY })).value
    : window.localStorage.getItem(AUTH_STORAGE_KEY),
  set: async (value) => {
    if (Capacitor.isNativePlatform()) await Preferences.set({ key: AUTH_STORAGE_KEY, value });
    else window.localStorage.setItem(AUTH_STORAGE_KEY, value);
  },
  remove: async () => {
    if (Capacitor.isNativePlatform()) await Preferences.remove({ key: AUTH_STORAGE_KEY });
    else window.localStorage.removeItem(AUTH_STORAGE_KEY);
  },
};

const isAppleSession = (session: AppleSession | null): session is AppleSession =>
  session?.provider === 'apple'
  && typeof session.userIdentifier === 'string'
  && session.userIdentifier.length > 0
  && (session.email === undefined || typeof session.email === 'string');

export const isApplePrivateRelayEmail = (email: string) =>
  email.trim().toLowerCase().endsWith('@privaterelay.appleid.com');

const appleErrorCode = (cause: unknown) => {
  if (cause && typeof cause === 'object' && 'code' in cause && typeof cause.code === 'string') return cause.code;
  return cause instanceof Error ? cause.message : '';
};

const diagnosticFields = [
  'code',
  'message',
  'nativeErrorDomain',
  'nativeErrorCode',
  'nativeErrorDescription',
  'nativeFailureReason',
  'nativeRecoverySuggestion',
  'nativeUnderlyingError1Domain',
  'nativeUnderlyingError1Code',
  'nativeUnderlyingError1Description',
  'nativeUnderlyingError2Domain',
  'nativeUnderlyingError2Code',
  'nativeUnderlyingError2Description',
  'nativeUnderlyingError3Domain',
  'nativeUnderlyingError3Code',
  'nativeUnderlyingError3Description',
  'osStatus',
] as const;

const recordOf = (value: unknown): Record<string, unknown> | null =>
  value != null && typeof value === 'object' ? value as Record<string, unknown> : null;

export const formatAppleSignInDiagnostic = (cause: unknown) => {
  const error = recordOf(cause);
  const data = recordOf(error?.data);
  const lines = diagnosticFields.flatMap((field) => {
    const value = error?.[field] ?? data?.[field];
    return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
      ? [`${field}: ${String(value)}`]
      : [];
  });
  return [...new Set(lines)].join('\n');
};

const showNativeDiagnostics = import.meta.env.VITE_SHOW_NATIVE_AUTH_DIAGNOSTICS === 'true';
const appleSignInError = (cause: unknown) => {
  if (appleErrorCode(cause).includes('AUTH_CANCELLED')) {
    return new Error('Apple 로그인이 취소되었습니다. 다시 시도하거나 로그인 없이 시작해 주세요.');
  }
  const diagnostic = showNativeDiagnostics ? formatAppleSignInDiagnostic(cause) : '';
  return new Error([
    'Apple 로그인에 실패했습니다. 연결을 확인한 뒤 다시 시도하거나 로그인 없이 시작해 주세요.',
    diagnostic ? `\nNative diagnostic\n${diagnostic}` : '',
  ].filter(Boolean).join('\n'));
};

export const createAuthService = (dependencies: AuthServiceDependencies = {
  isNative: Capacitor.isNativePlatform(),
  storage: defaultStorage,
  appleSignIn: AppleSignIn,
  exchangeAppleIdentity,
  fetchAccountEntitlement,
  revokeAccountSession,
  deleteAccountData,
  configureAccountDataSession,
}) => ({
  restore: async (): Promise<AuthSession | null> => {
    const stored = await dependencies.storage.get();
    if (stored) {
      try {
        if ((JSON.parse(stored) as Partial<AuthSession>).provider === 'guest') {
          dependencies.configureAccountDataSession(null);
          return { provider: 'guest' };
        }
      } catch {
        await dependencies.storage.remove();
      }
    }
    if (!dependencies.isNative) return null;
    const { session } = await dependencies.appleSignIn.restoreSession();
    if (!isAppleSession(session)) {
      dependencies.configureAccountDataSession(null);
      return null;
    }
    const server = await dependencies.appleSignIn.getServerSession();
    if (!server.session || Date.parse(server.session.expiresAt) <= Date.now()) {
      await dependencies.appleSignIn.clearServerSession();
      dependencies.configureAccountDataSession(null);
      return null;
    }
    dependencies.configureAccountDataSession(server.session);
    return { ...session, accountSession: server.session };
  },

  continueAsGuest: async (): Promise<GuestSession> => {
    const session: GuestSession = { provider: 'guest' };
    await dependencies.storage.set(JSON.stringify(session));
    dependencies.configureAccountDataSession(null);
    return session;
  },

  signInWithApple: async (): Promise<AppleSession> => {
    if (!dependencies.isNative) {
      throw new Error('Apple로 계속하기는 iPhone 또는 iPad 앱에서 사용할 수 있습니다. 로그인 없이 시작할 수 있습니다.');
    }
    let session: AppleSession | null;
    let identityToken: string | undefined;
    try {
      ({ session, identityToken } = await dependencies.appleSignIn.signIn());
    } catch (cause) {
      throw appleSignInError(cause);
    }
    if (!isAppleSession(session)) throw new Error('Apple 로그인이 완료되지 않았습니다. 다시 시도하거나 로그인 없이 시작해 주세요.');
    if (!identityToken) throw new Error('Apple 계정 확인 정보가 없습니다. Apple 로그인을 다시 시도해 주세요.');
    const accountSession = await dependencies.exchangeAppleIdentity(identityToken);
    await dependencies.appleSignIn.setServerSession(accountSession);
    dependencies.configureAccountDataSession(accountSession);
    await dependencies.storage.remove();
    return { ...session, accountSession };
  },

  refreshAccount: async (session: AppleSession): Promise<AppleSession> => {
    if (!dependencies.isNative || !session.accountSession) {
      throw new Error('Apple 계정 상태를 갱신할 수 없습니다.');
    }
    const accountSession = await dependencies.fetchAccountEntitlement(session.accountSession);
    await dependencies.appleSignIn.setServerSession(accountSession);
    dependencies.configureAccountDataSession(accountSession);
    return { ...session, accountSession };
  },

  logout: async (session: AuthSession): Promise<void> => {
    if (session.provider === 'apple' && dependencies.isNative) {
      try {
        if (session.accountSession) {
          try { await dependencies.revokeAccountSession(session.accountSession); } catch { /* Local logout still completes offline. */ }
        }
        await dependencies.appleSignIn.clearServerSession();
        await dependencies.appleSignIn.clearSession();
      } catch {
        throw new Error('Apple 시작 상태를 지우지 못했습니다. 잠시 후 다시 시도해 주세요.');
      }
    }
    dependencies.configureAccountDataSession(null);
    await dependencies.storage.remove();
  },

  deleteAccount: async (session: AppleSession): Promise<void> => {
    if (!dependencies.isNative || !session.accountSession) {
      throw new Error('삭제할 Apple 계정 연결을 찾지 못했습니다.');
    }
    await dependencies.deleteAccountData(session.accountSession);
    await dependencies.appleSignIn.clearServerSession();
    await dependencies.appleSignIn.clearSession();
    dependencies.configureAccountDataSession(null);
    await dependencies.storage.remove();
  },
});

export const authService = createAuthService();
